import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { Assignment, Binder, Contact } from '@apaq/orgkit-js';
import log from 'loglevel';

@Component({
  selector: 'ws-workplan',
  templateUrl: './workplan.component.html',
  styleUrls: ['./workplan.component.scss']
})
export class WorkplanComponent  {

  selectedAssignments: string[] = [];
  
  _assignments?: Assignment[] = [];
  _selectable = false;
  _value?: Assignment;
  @Input() groups: Binder[] = [];
  _contacts?: Contact[];
  longDueAssignments?: Assignment[];
  ordinaryAssignments?: Assignment[];
  skippedAssignments?: Assignment[];
  titleMap = new Map<string, string>();
  groupedIdsMap = new Map<string, string[]>();
  @Output() valueChange = new EventEmitter<Assignment>();
  @Output() selectionChange = new EventEmitter<string[]>()

  get value(): Assignment {
    return this._value!;
  }

  @Input() 
  set value(value: Assignment | undefined) {
    this._value = value;
  }

  get assignments() {
    return this._assignments;
  }

  @Input() 
  set assignments(value: Assignment[] | undefined) {
    this._assignments = value;

    if(this.assignments != null) {
      this.longDueAssignments = this.assignments.filter(a => a.plan.allDay === true && a.action !== 'Skipped');
      this.ordinaryAssignments = this.assignments.filter(a => a.plan.allDay === false && a.action !== 'Skipped');
      this.skippedAssignments = this.assignments.filter(a => a.action === 'Skipped');
      this.selectedAssignments = [];

      this.buildTitleMap();
      this.buildGroupedIdsMap();
    }
  }

  get contacts() {
    return this._contacts!;
  }

  @Input()
  set contacts(value: Contact[] | undefined) {
    this._contacts = value;

    this.buildTitleMap();
    this.buildGroupedIdsMap();
  }

  get selectable() {
    return this._selectable;
  }

  @Input()
  set selectable(value: boolean) {
    this._selectable = value;
    this.selectedAssignments = [];
  }

  buildTitleMap() {
    const titleMap = new Map<string, string>();
    let prevGroupId: string | undefined;
    let count = 0;
    this._assignments?.forEach((a) => {
      if(a.plan.allDay || a.action === 'Skipped') {
        return;
      }
      count++;

      var contact = this.contacts?.find(c => c.id === a.contactId);
      if(contact?.planningGroupBinderId === prevGroupId && count > 1) {
        return;
      }

      let group = this.groups?.find(g => contact?.planningGroupBinderId === g.id);
      if(group == null) {
        titleMap.set(a.id, $localize`General assignments`);
      } else {
        titleMap.set(a.id, group.title);
      } 
      prevGroupId = contact?.planningGroupBinderId;
    });
    this.titleMap = titleMap;
  }

  buildGroupedIdsMap() {
    const map = new Map<string, string[]>();
    let accIds: string[] = []
    let initialAsgmtId: string | null = null;
    let prevGroupId: string | undefined;
    let count = 0;
    this._assignments?.forEach((a) => {
      if(a.plan.allDay || a.action === 'Skipped') {
        return;
      }
      count++;
     
      
      var contact = this.contacts?.find(c => c.id === a.contactId);
      if(contact?.planningGroupBinderId === prevGroupId && count > 1) {
        accIds.push(a.id);
        return;
      }

      if(initialAsgmtId != null) {
        map.set(initialAsgmtId, accIds);
        accIds = [];
      }

      initialAsgmtId = a.id;
      accIds.push(a.id);
      

      prevGroupId = contact?.planningGroupBinderId;
    });

    if(initialAsgmtId != null) {
      map.set(initialAsgmtId, accIds);
    }
    this.groupedIdsMap = map;
  }

  hasMultipleTitles() {
    return this.titleMap.size > 1;
  }

  hasTitle(assignment: Assignment) {
    return this.titleMap.get(assignment.id) != null;
  }

  getTitle(assignment: Assignment) {
    return this.titleMap.get(assignment.id);
  }
  
  getIdsForGroup(assignment: Assignment) {
    return this.groupedIdsMap.get(assignment.id)?.join(',');
  }
  
  dragComponentGenerator(): (ev: DragEvent) => HTMLElement | undefined {
    
    return (ev) => {
      const el = document.createElement('sl-badge');
      el.style.zIndex = 'var(--sl-z-index-tooltip)';
      const count = this.selectedAssignments.length;
      let assignments: Assignment[];

      if(count === 0) {
        ev.dataTransfer!.effectAllowed = 'move';
        
        /* We resolve the closest list item from mouse coordinates. */
        let item = document.elementFromPoint(ev.clientX, ev.clientY)?.closest('sl-list-item,sl-list-divider');
        if(item?.tagName === 'SL-LIST-DIVIDER') {
          /* We use the comma-seperated ids specified on the element to find the assignment */
          const ids = item.getAttribute('id')?.split(',') ?? [];
          assignments = this.assignments?.filter(a => a.action === 'None' && a.plan.affixment.state !== 'Locked' && ids.indexOf(a.id) >= 0) ?? [];
          el.innerHTML = assignments.length === 1 ? $localize`1 assignment` : $localize`${assignments.length} assignments`;
        } else if(item?.tagName === 'SL-LIST-ITEM') {
          /* We use the id specified on the element to find the assignment */
          const id = item.getAttribute('id');
          const assignment = this.assignments?.filter(a => a.action === 'None' && a.plan.affixment.state !== 'Locked').find(a => a.id === id);
          if(assignment == null) {
            ev.preventDefault();
            return undefined
          }
          assignments = [assignment!];
          el.innerHTML = $localize`1 assignment`;
        } else {
          ev.preventDefault();
          return undefined
        }
      } else {
        assignments = this.assignments?.filter(a => a.plan.affixment.state !== 'Locked' && this.selectedAssignments.indexOf(a.id) >= 0) ?? [];
        el.innerHTML = assignments.length === 1 ? $localize`1 assignment` : $localize`${assignments.length} assignments`;
      }

      if(!(assignments?.length > 0)) {
        ev.preventDefault();
        return undefined
      }

      ev.dataTransfer!.setData('application/assignment-list-json', JSON.stringify(assignments));
      el.style.position = 'fixed';
      document.body.appendChild(el);
      return el;
    }
  }

  onSelection(value: boolean, assignment: Assignment) {
    if(value === true) {
      this.selectedAssignments.push(assignment.id);
    } else {
      const index = this.selectedAssignments.indexOf(assignment.id);
      if(index >= 0) {
        this.selectedAssignments.splice(index, 1);
      }
    }
    this.selectionChange.emit(this.selectedAssignments);
  }

  onListSelection(event: CustomEvent) {
    const assignmentId = event.detail;
    event.stopPropagation();
    const newValue = this.assignments?.find(a => a.id === assignmentId)!;
    if(this.value != newValue) {
      log.info(`onListSelection: ${newValue}`);
      this.value = newValue;
      this.valueChange.emit(this.value);
    }
  }

  

}
